import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { Identifier } from 'web-ui-components/lib/typography/modifiers';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';

import { paymentReviewContent } from '../PaymentReview/PaymentReview.content';
import { ErrorValidationMessage } from '../ErrorValidationMessage/ErrorValidationMessage';
import { StandardLabel } from 'web-ui-components/lib/typography/labels';
import { SmallestBody } from 'web-ui-components/lib/typography/body';
import { paymentReviewIconStyles } from '../PaymentReview/styles/styles';
import { cx } from 'react-emotion';

const { noAvailableAuthorisersErrorMessage } = paymentReviewContent;

const SubmittedTransactionTable = ({
	hasNoAvailableAuthorisers,
	paymentsTotal,
	showSidebar,
	sidebarIconType,
	sidebarTitle,
	sidebarContent,
	sidebarShowButton,
	sidebarButtonOnClick,
	sidebarButtonIcon,
	sidebarButtonText,
	children,
	labels: {
		toLabel,
		fromLabel,
		amtLabel,
		refLabel,
		dateLabel,
		typeLabel,
		changeAuthoriserButton,
		viewAuthoriserButton,
		selectAuthoriserButton,
	},
	onSelectAuthorisers,
	requiresAuth,
	isDefaultAuthorisers,
	authorisers,
	hasGroupApprovalsEnabled,
}) => {
	const wrapperClasses = 'c-o-submitted-transaction-table c-o-submitted-transaction-table--warning';
	const sidebarClasses = cx('c-o-submitted-transaction-table__status c-o-submitted-transaction-table--warning', [
		paymentReviewIconStyles,
	]);

	const showAuthButton = requiresAuth && !isDefaultAuthorisers && !hasNoAvailableAuthorisers;
	const showHasNoAvailableAuthorisersError = requiresAuth && !isDefaultAuthorisers && hasNoAvailableAuthorisers;

	const selectAuthoriserButtonLabel = authorisers.length ? changeAuthoriserButton : selectAuthoriserButton;

	const getAuthoriserButton = hasGroupApprovalsEnabled ? viewAuthoriserButton : selectAuthoriserButtonLabel;

	return (
		<div className={wrapperClasses}>
			<div className="c-o-submitted-transaction-table__transactions">
				<div className="c-m-payment-review c-m-payment-review--header">
					<div className="c-m-payment-review__row c-m-payment-review__row--double">
						<span className="c-m-payment-review__label">{fromLabel}</span>
					</div>

					<div className="c-m-payment-review__row c-m-payment-review__row--double">
						<Identifier.Breaking>
							<span className="c-m-payment-review__label">{toLabel}</span>
						</Identifier.Breaking>
					</div>

					<div className="c-m-payment-review__row">
						<span className="c-m-payment-review__label">{amtLabel}</span>
					</div>

					<div className="c-m-payment-review__row c-m-payment-review__row--ref">
						<span className="c-m-payment-review__label">{refLabel}</span>
					</div>

					<div className="c-m-payment-review__row">
						<span className="c-m-payment-review__label">{dateLabel}</span>
					</div>

					<div className="c-m-payment-review__row">
						<span className="c-m-payment-review__label">{typeLabel}</span>
					</div>
				</div>
				<div className="c-o-submitted-transaction-table__payment-rows">
					{children}
					<div className="c-o-submitted-transaction-table__total">
						<span>Total: £{paymentsTotal}</span>
					</div>
				</div>
			</div>
			{showSidebar && (
				<div className={sidebarClasses}>
					<div className="c-o-card c-o-card--bordered">
						<Icon iconType={sidebarIconType} />
						<StandardLabel isFlush>{sidebarTitle}</StandardLabel>
						<SmallestBody isFlush>{sidebarContent}</SmallestBody>
						{showHasNoAvailableAuthorisersError && (
							<ErrorValidationMessage data-test-id="no-available-error-authorisers-error-message">
								{noAvailableAuthorisersErrorMessage}
							</ErrorValidationMessage>
						)}
						{showAuthButton && (
							<NavigationButton.Tertiary
								data-test-id="batch-view-authorisers-button"
								isInline
								labelText={getAuthoriserButton}
								onClick={onSelectAuthorisers}
							/>
						)}
						{sidebarShowButton && (
							<Button display="primary" onClick={sidebarButtonOnClick}>
								{sidebarButtonText} <Icon iconType={sidebarButtonIcon} className="c-a-button__icon" direction="right" />
							</Button>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

SubmittedTransactionTable.defaultProps = {
	hasNoAvailableAuthorisers: false,
	hasStatus: false,
	requiresAuth: false,
};

SubmittedTransactionTable.propTypes = {
	hasNoAvailableAuthorisers: PropTypes.bool,
	paymentsTotal: PropTypes.string.isRequired,
	showSidebar: PropTypes.bool,
	sidebarIconType: PropTypes.string,
	sidebarTitle: PropTypes.string,
	sidebarContent: PropTypes.string,
	sidebarShowButton: PropTypes.bool,
	sidebarButtonOnClick: PropTypes.func,
	sidebarButtonIcon: PropTypes.string,
	sidebarButtonText: PropTypes.string,
	children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
	labels: PropTypes.shape({
		fromLabel: PropTypes.string,
		toLabel: PropTypes.string,
		amtLabel: PropTypes.string,
		refLabel: PropTypes.string,
		dateLabel: PropTypes.string,
		typeLabel: PropTypes.string,
	}).isRequired,
	onSelectAuthorisers: PropTypes.func,
	requiresAuth: PropTypes.bool,
	isDefaultAuthorisers: PropTypes.bool,
	onlyShowAuthorisers: PropTypes.bool,
};

export default SubmittedTransactionTable;
